// ═══════════════════════════════════════════════════════════════════════════════
// Task / home stock translated content display helpers
// ═══════════════════════════════════════════════════════════════════════════════

import {
  USER_ROLE,
  resolveBilingualInventoryItemEmployerFallback,
  type InventoryItemNameTranslation,
  type InventoryLocationTranslationMeta,
  type InventorySubmissionItemView,
  type Language,
  type Task,
  type TaskInstanceTranslationMeta,
  type TaskInstanceWithSubmission,
  type TaskTranslationMeta,
  type TodayTodoItem,
} from '@fdh/shared';
import { resolveTranslatedDisplayText } from './translationDisplay';

const normalizeComparable = (value: string): string => value.trim().toLowerCase();

export const resolveTodoItemDisplayTitle = (
  item: TodayTodoItem,
  showOriginal: boolean,
): string =>
  resolveTranslatedDisplayText(item.title, item.translation?.fields.title, showOriginal);

export const resolveTodoItemDisplayDescription = (
  item: TodayTodoItem,
  showOriginal: boolean,
): string =>
  resolveTranslatedDisplayText(item.description, item.translation?.fields.description, showOriginal);

export const resolveTodoItemDisplayNeedsUpdateReason = (
  item: TodayTodoItem,
  showOriginal: boolean,
): string =>
  resolveTranslatedDisplayText(
    item.needsUpdateReason,
    item.translation?.fields.needsUpdateReason,
    showOriginal,
  );

export const resolveTaskDisplayTitle = (
  task: Pick<Task, 'title'> & { translation?: TaskTranslationMeta },
  showOriginal: boolean,
): string =>
  resolveTranslatedDisplayText(task.title, task.translation?.fields.title, showOriginal);

export const resolveTaskDisplayDescription = (
  task: Pick<Task, 'description'> & { translation?: TaskTranslationMeta },
  showOriginal: boolean,
): string =>
  resolveTranslatedDisplayText(task.description, task.translation?.fields.description, showOriginal);

export const resolveInstanceDisplayNeedsUpdateReason = (
  reason: string,
  translation: TaskInstanceTranslationMeta | undefined,
  showOriginal: boolean,
): string =>
  resolveTranslatedDisplayText(reason, translation?.fields.needsUpdateReason, showOriginal);

export const resolveInventoryItemNameForDisplay = (params: {
  item: Pick<
    InventorySubmissionItemView,
    'itemName' | 'itemNameEmployerDisplay' | 'itemNameHelperSnapshot' | 'translation'
  >;
  viewerRole: string;
  viewerLanguage?: Language;
  showOriginal: boolean;
}): string => {
  const { item, viewerRole, viewerLanguage, showOriginal } = params;
  if (showOriginal) {
    return item.itemName;
  }
  if (viewerRole === USER_ROLE.EMPLOYER) {
    if (item.translation?.hasTranslation && item.translation.translatedItemName.trim() !== '') {
      return item.translation.translatedItemName.trim();
    }
    const unchanged =
      (item.itemNameHelperSnapshot ?? '').trim() !== '' &&
      normalizeComparable(item.itemName) === normalizeComparable(item.itemNameHelperSnapshot ?? '');
    const employerDisplay = (item.itemNameEmployerDisplay ?? '').trim();
    if (unchanged && employerDisplay !== '') {
      if (viewerLanguage && employerDisplay !== item.itemName.trim()) {
        return resolveBilingualInventoryItemEmployerFallback({
          itemName: item.itemName,
          itemNameEmployerDisplay: employerDisplay,
          viewerLanguage,
        });
      }
      return employerDisplay;
    }
    return item.itemName;
  }
  if (item.translation?.hasTranslation && item.translation.translatedItemName.trim() !== '') {
    return item.translation.translatedItemName.trim();
  }
  return item.itemName;
};

type SubmissionLocationView = {
  locationName: string;
  noItemsCategories?: string[];
  locationTranslation?: InventoryLocationTranslationMeta;
};

export const resolveTaskWizardLocationDisplay = (
  task: Pick<Task, 'inventoryLocations'> & { translation?: TaskTranslationMeta },
  locationName: string,
  showOriginal: boolean,
): string => {
  const trimmed = locationName.trim();
  const locs = (task.inventoryLocations ?? []).map((l) => l.trim());
  const index = locs.findIndex((l) => l === trimmed);
  if (index < 0) {
    return locationName;
  }
  const translated = task.translation?.fields.inventoryLocations?.[index];
  return resolveTranslatedDisplayText(locationName, translated, showOriginal);
};

export const resolveTaskWizardCategoryDisplay = (
  task: Pick<Task, 'inventoryCategories'> & { translation?: TaskTranslationMeta },
  categoryName: string,
  showOriginal: boolean,
): string => {
  const trimmed = categoryName.trim();
  const cats = (task.inventoryCategories ?? []).map((c) => c.trim());
  const index = cats.findIndex((c) => c === trimmed);
  if (index < 0) {
    return categoryName;
  }
  const translated = task.translation?.fields.inventoryCategories?.[index];
  return resolveTranslatedDisplayText(categoryName, translated, showOriginal);
};

export const resolveSubmissionLocationNameDisplay = (
  loc: SubmissionLocationView,
  showOriginal: boolean,
): string =>
  resolveTranslatedDisplayText(
    loc.locationName,
    loc.locationTranslation?.translatedLocationName,
    showOriginal,
  );

export const resolveSubmissionNoItemsCategoryDisplay = (
  loc: SubmissionLocationView,
  categoryOriginal: string,
  showOriginal: boolean,
): string => {
  const originals = (loc.noItemsCategories ?? []).map((c) => c.trim()).filter((c) => c !== '');
  const idx = originals.findIndex((c) => c === categoryOriginal.trim());
  if (idx < 0) {
    return categoryOriginal;
  }
  const translated = loc.locationTranslation?.translatedNoItemsCategories?.[idx];
  return resolveTranslatedDisplayText(categoryOriginal, translated, showOriginal);
};

export const resolveSubmissionItemCategoryDisplay = (
  categoryName: string,
  categoryTranslation: InventoryItemNameTranslation | undefined,
  task: Pick<Task, 'inventoryCategories'> & { translation?: TaskTranslationMeta },
  showOriginal: boolean,
): string => {
  if (categoryTranslation?.hasTranslation && categoryTranslation.translatedItemName.trim() !== '') {
    return resolveTranslatedDisplayText(
      categoryName,
      categoryTranslation.translatedItemName,
      showOriginal,
    );
  }
  return resolveTaskWizardCategoryDisplay(task, categoryName, showOriginal);
};

export const resolveHelperInventoryCategoryDisplay = (params: {
  category: string;
  loc: SubmissionLocationView | undefined;
  task: Pick<Task, 'inventoryCategories'> & { translation?: TaskTranslationMeta };
  items: Pick<InventorySubmissionItemView, 'categoryName' | 'categoryTranslation'>[];
  showOriginal: boolean;
}): string => {
  const { category, loc, task, items, showOriginal } = params;
  const trimmed = category.trim();
  if (loc) {
    const isNoItems = (loc.noItemsCategories ?? []).some((c) => c.trim() === trimmed);
    if (isNoItems) {
      return resolveSubmissionNoItemsCategoryDisplay(loc, category, showOriginal);
    }
  }
  const item = items.find((it) => it.categoryName.trim() === trimmed);
  return resolveSubmissionItemCategoryDisplay(
    category,
    item?.categoryTranslation,
    task,
    showOriginal,
  );
};

export const todoListHasTranslations = (tasks: TodayTodoItem[]): boolean =>
  tasks.some((task) => task.translation?.hasTranslation);

export const taskInstanceHasHelperTranslations = (instance: TaskInstanceWithSubmission): boolean => {
  if (instance.task.translation?.hasTranslation) {
    return true;
  }
  if (instance.translation?.hasTranslation) {
    return true;
  }
  const locations = instance.inventorySubmission?.locations ?? [];
  for (const loc of locations) {
    if (loc.locationTranslation?.hasTranslation) {
      return true;
    }
    for (const item of loc.items) {
      if (item.translation?.hasTranslation || item.categoryTranslation?.hasTranslation) {
        return true;
      }
    }
  }
  return false;
};

export const inventorySubmissionHasEmployerItemNameTranslations = (
  instance: TaskInstanceWithSubmission,
): boolean => {
  const locations = instance.inventorySubmission?.locations ?? [];
  for (const loc of locations) {
    for (const item of loc.items) {
      const unchanged =
        (item.itemNameHelperSnapshot ?? '').trim() !== '' &&
        normalizeComparable(item.itemName) === normalizeComparable(item.itemNameHelperSnapshot ?? '');
      if (unchanged && (item.itemNameEmployerDisplay ?? '').trim() !== '') {
        return true;
      }
      if (item.translation?.hasTranslation) {
        return true;
      }
    }
  }
  return false;
};

export const resolveTranslationTargetLanguageLabel = (
  t: (key: string) => string,
  language: Language | undefined,
  fallbackLanguage: Language,
): string => {
  const lang = language ?? fallbackLanguage;
  if (lang === 'zh-Hant') return t('translation.lang.zhHant');
  if (lang === 'zh-Hans') return t('translation.lang.zhHans');
  if (lang === 'en') return t('translation.lang.en');
  if (lang === 'id') return t('translation.lang.id');
  if (lang === 'fil') return t('translation.lang.fil');
  return t('translation.lang.en');
};
